import { forwardRef, FunctionComponent, PropsWithChildren, ReactNode, Ref } from 'react';
import styled, { css } from 'styled-components';

import { defaultThemeProp } from '../../theme';
import Flex from '../Flex';
import { BaseProps, ForwardProps } from '../../types';
import { StyledPopover } from '../Popover';

import { StyledCard } from './Card';
import { StyledCardContent } from './CardContent';

export interface CardHeaderProps extends BaseProps {
  /** The Card header content. */
  children: ReactNode;
  /** Action Buttons that will render within the header. */
  actions?: ReactNode;
  /** Ref for the wrapping element. */
  ref?: Ref<HTMLDivElement>;
}

export const StyledCardHeader = styled.header<CardHeaderProps>(({ theme, onClick }) => {
  return css`
    ${StyledPopover} &, &:not(${StyledCard} ${StyledCard} > &) {
      padding: calc(1.5 * ${theme.base.spacing}) calc(${theme.components.card.padding} * 2);

      + ${StyledCardContent} {
        padding-block-start: 0;
      }

      &:hover {
        ${onClick ? 'cursor: pointer;' : undefined}
      }
    }
  `;
});

StyledCardHeader.defaultProps = defaultThemeProp;

const CardHeader: FunctionComponent<CardHeaderProps & ForwardProps> = forwardRef(
  (
    { children, actions, ...restProps }: PropsWithChildren<CardHeaderProps>,
    ref: CardHeaderProps['ref']
  ) => {
    return (
      <Flex
        container={{ alignItems: 'center', justify: 'between' }}
        as={StyledCardHeader}
        {...restProps}
        ref={ref}
      >
        {actions ? (
          <>
            <Flex container={{ alignItems: 'center' }} item={{ grow: 1 }}>
              {children}
            </Flex>
            <div>{actions}</div>
          </>
        ) : (
          children
        )}
      </Flex>
    );
  }
);

CardHeader.displayName = 'CardHeader';
export default CardHeader;
